import { LiveclassStudent } from "@oneapp/data";
import { buildLiveclassStudentSyncPayload } from "../liveclassStudentSyncPayload";
import { useAuthStore } from "../../store/useAuthStore";
import {
  clearInfinityLearnHandoffCookies,
  readAccountsOfLoggedInUserCookie,
  readInfinityLearnCookie,
  readProfilesOfLoggedInUserCookie,
} from "./cookies";
import { fetchEnrolledProgramsForHandoff } from "./fetchEnrolledPrograms";
import { mapCookiesToAuthState } from "./mapCookiesToAuthState";

const HANDOFF_LOG_PREFIX = "[IL Handoff]";

/**
 * Hydrates {@link useAuthStore} from IL portal cookies when the user lands on oneapp
 * already logged in on the Infinity Learn website (IL profile, not SCA admission flow).
 */
export async function bootstrapInfinityLearnHandoff(): Promise<boolean> {
  const ilCookie = readInfinityLearnCookie();
  if (!ilCookie) {
    return false;
  }

  const { accessToken } = ilCookie;
  const authState = useAuthStore.getState();

  if (authState.token === accessToken && authState.profilesData) {
    clearInfinityLearnHandoffCookies();
    return true;
  }

  const accountsCookie = readAccountsOfLoggedInUserCookie();
  const profilesCookie = readProfilesOfLoggedInUserCookie();
  const mapped = mapCookiesToAuthState(
    ilCookie,
    accountsCookie,
    profilesCookie,
  );

  authState.setAccountsData(mapped.accountsData);
  authState.setProfilesData(mapped.profilesData);
  authState.setLoginMethod("phone_otp");

  try {
    const programs = await fetchEnrolledProgramsForHandoff(
      mapped.accessToken,
      mapped.profilesData,
    );
    if (programs.length > 0) {
      authState.setEnrolledPrograms(programs);
    } else {
      console.warn(
        `${HANDOFF_LOG_PREFIX} No enrolled programs returned; navigation may use defaults.`,
      );
    }
  } catch (error) {
    console.warn(
      `${HANDOFF_LOG_PREFIX} Enrolled programs fetch failed:`,
      error,
    );
  }

  authState.login(mapped.accessToken, mapped.user, undefined, true, {
    skipAinaIntroPrime: true,
  });

  try {
    const payload = buildLiveclassStudentSyncPayload();
    if (payload) {
      await LiveclassStudent.create({
        clientName: "liveclass",
        data: payload,
      });
    }
  } catch (error) {
    console.warn(`${HANDOFF_LOG_PREFIX} Liveclass student sync failed:`, error);
  }

  clearInfinityLearnHandoffCookies();

  console.info(
    `${HANDOFF_LOG_PREFIX} Session restored from INFINITY_LEARN cookie.`,
  );
  return true;
}
